/* eslint-disable react/prop-types */
import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import TrebbleSwitch from "../common/TrebbleSwitch";
import TrebbleButton from "../common/TrebbleButton";
import TrebbleCard from "../common/TrebbleCard";
import TrebbleInput from "../common/TrebbleInput";
import TrebbleTooltip from "../common/TrebbleTooltip";
import TrebbleDropdown from "../common/TrebbleDropdown";
import TrebbleSegmented from "../common/TrebbleSegmented";
import { normalizeProfileImageUrlForHttps } from "models/helper/UrlNormalizer";
import { isLocalhostHostname, getDefaultLocalApiBaseUrl } from "models/helper/LocalhostHelper";
import "./styles.css";

// Section identifiers
const SECTION_PROFILE = "profile";
const SECTION_SECURITY = "security";
const SECTION_NOTIFICATIONS = "notifications";
const SECTION_CONNECTIONS = "connections";
const SECTION_WS_GENERAL = "ws-general";
const SECTION_WS_MEMBERS = "ws-members";
const SECTION_WS_BILLING = "ws-billing";
const SECTION_WS_USAGE = "ws-usage";

const VALID_SECTIONS = new Set([
  SECTION_PROFILE, SECTION_SECURITY, SECTION_NOTIFICATIONS, SECTION_CONNECTIONS,
  SECTION_WS_GENERAL, SECTION_WS_MEMBERS, SECTION_WS_BILLING, SECTION_WS_USAGE,
]);

// Official Model Context Protocol logo (MIT licensed):
// https://commons.wikimedia.org/wiki/File:Model_Context_Protocol_logo.svg
// Uses currentColor so it inherits the sidebar/badge color like the pe-7s icon font does.
const McpLogoIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 180 180" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M18 84.8528L85.8822 16.9706C95.2548 7.59798 110.451 7.59798 119.823 16.9706V16.9706C129.196 26.3431 129.196 41.5391 119.823 50.9117L68.5581 102.177" stroke="currentColor" strokeWidth="14" strokeLinecap="round" />
    <path d="M69.2652 101.47L119.823 50.9117C129.196 41.5391 144.392 41.5391 153.765 50.9117L154.118 51.2652C163.491 60.6378 163.491 75.8338 154.118 85.2063L92.7248 146.6C89.6006 149.724 89.6006 154.789 92.7248 157.913L105.331 170.52" stroke="currentColor" strokeWidth="14" strokeLinecap="round" />
    <path d="M102.853 33.9411L52.6482 84.1457C43.2756 93.5183 43.2756 108.714 52.6482 118.087V118.087C62.0208 127.459 77.2167 127.459 86.5893 118.087L136.794 67.8822" stroke="currentColor" strokeWidth="14" strokeLinecap="round" />
  </svg>
);

const ACCOUNT_NAV_ITEMS = [
  { key: SECTION_PROFILE, icon: "pe-7s-user", i18nKey: "NAV_PROFILE", fallback: "Profile" },
  { key: SECTION_SECURITY, icon: "pe-7s-lock", i18nKey: "NAV_SECURITY", fallback: "Security" },
  { key: SECTION_NOTIFICATIONS, icon: "pe-7s-bell", i18nKey: "NAV_NOTIFICATIONS", fallback: "Notifications" },
  { key: SECTION_CONNECTIONS, IconComponent: McpLogoIcon, i18nKey: "NAV_CONNECTIONS", fallback: "Trebble MCP" },
];

// Workspace nav items — filtered by role at render time. The Members
// section is shown for ALL workspaces, including the synthesized default
// personal workspace (legacy creator-sub users). Free users still see the
// Members section but the Invite button is disabled with an upgrade
// tooltip; paid personal-workspace users get a real Invite click that
// triggers backend workspace materialization.
const WS_NAV_ITEMS_ALL = [
  { key: SECTION_WS_GENERAL, icon: "pe-7s-config", i18nKey: "NAV_GENERAL", fallback: "General" },
  { key: SECTION_WS_MEMBERS, icon: "pe-7s-users", i18nKey: "NAV_MEMBERS", fallback: "Members", roles: ["owner", "admin"], hideWhenPersonal: false },
  { key: SECTION_WS_BILLING, icon: "pe-7s-credit", i18nKey: "NAV_BILLING", fallback: "Billing", roles: ["owner"] },
  { key: SECTION_WS_USAGE, icon: "pe-7s-graph1", i18nKey: "NAV_USAGE", fallback: "Usage" },
];

const t = (i18nModule, key) => {
  if (window.getI18n && i18nModule) {
    return window.getI18n(i18nModule, key);
  }
  return key;
};

const COPY_TARGETS = {
  SERVER: "server",
  OAUTH: "oauth",
};

const normalizeUrlWithoutTrailingSlash = (value) => {
  return typeof value === "string" ? value.replace(/\/+$/, "") : "";
};

const joinUrlPath = (baseUrl, path) => {
  const normalizedBaseUrl = normalizeUrlWithoutTrailingSlash(baseUrl);
  const normalizedPath = path.charAt(0) === "/" ? path : "/" + path;
  return normalizedBaseUrl ? normalizedBaseUrl + normalizedPath : normalizedPath;
};

// API_BASE_URL is injected by webpack DefinePlugin as a string literal. Read it
// WITHOUT a `typeof process` guard: webpack replaces only the exact
// `process.env.API_BASE_URL` token and does not provide a browser `process`
// global, so a guard would short-circuit and silently drop the injected value.
// See the same pattern in TrebbleClientAPI.
const getConfiguredApiBaseUrl = () => normalizeUrlWithoutTrailingSlash(process.env.API_BASE_URL || "");

// Resolves the origin that serves MCP. Production has a dedicated subdomain
// (mcp.trebble.fm); dev/local and Heroku review-app backends don't have that
// subdomain provisioned, so they hit the API host directly. This is the base
// only — the MCP streamable-HTTP endpoint lives at /mcp and the OAuth metadata
// lives at /.well-known/* on this origin (never under /mcp).
const getMcpBaseUrl = () => {
  const configuredApiBaseUrl = getConfiguredApiBaseUrl();
  if (configuredApiBaseUrl) {
    return configuredApiBaseUrl;
  }

  if (typeof window === "undefined" || !window.location) {
    return "https://mcp.trebble.fm";
  }

  if (isLocalhostHostname(window.location.hostname)) {
    return normalizeUrlWithoutTrailingSlash(getDefaultLocalApiBaseUrl());
  }

  const protocolPrefix = (window.trebble && window.trebble.usedProtocolPrefix)
    || (window.location.protocol === "http:" ? "http://" : "https://");
  return protocolPrefix + "mcp.trebble.fm";
};

// Production mcp.trebble.fm sits behind a facade that serves MCP at the root
// origin and advertises the origin as the canonical OAuth resource, so the
// cleaner bare-origin URL is what clients should use. A configured
// API_BASE_URL (Heroku review-app) or a localhost backend hits the origin
// directly (no facade), where MCP is served at /mcp — so those keep the /mcp path.
const mcpBaseServedAtRoot = () => {
  if (getConfiguredApiBaseUrl()) {
    return false;
  }

  if (typeof window === "undefined" || !window.location) {
    return true;
  }

  return !isLocalhostHostname(window.location.hostname);
};

// The MCP server endpoint clients connect to.
const getMcpServerUrl = () => {
  const base = getMcpBaseUrl();
  return mcpBaseServedAtRoot() ? base : joinUrlPath(base, "/mcp");
};

// OAuth authorization-server metadata is served at the origin root, NOT under
// /mcp (the /mcp-prefixed path 404s), so derive it from the base origin.
const getMcpOAuthMetadataUrl = () => joinUrlPath(getMcpBaseUrl(), "/.well-known/oauth-authorization-server");

const copyTextToClipboard = (text) => {
  if (typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
    return navigator.clipboard.writeText(text);
  }

  if (typeof document === "undefined" || !document.body) {
    return Promise.reject(new Error("Clipboard is not available"));
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "readonly");
  textArea.style.position = "fixed";
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  let didCopy = false;
  try {
    if (typeof document.execCommand === "function") {
      didCopy = document.execCommand("copy");
    }
  } catch (error) {
    didCopy = false;
  } finally {
    document.body.removeChild(textArea);
  }

  return didCopy ? Promise.resolve() : Promise.reject(new Error("Copy failed"));
};

const getInitials = (username) => {
  if (!username) return "?";
  const parts = username.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].substring(0, 2).toUpperCase();
};

const ROLE_BADGES = {
  owner: { label: "Owner", color: "#e9605d" },
  admin: { label: "Admin", color: "#009688" },
  editor: { label: "Editor", color: "#4A90D9" },
  reviewer: { label: "Reviewer", color: "#7E57C2" },
};
const UNKNOWN_VALUE = "--";

const hasDisplayValue = (value) => value !== undefined && value !== null && value !== "";

const isFiniteMetric = (value) => typeof value === "number" && Number.isFinite(value);

const isUnlimitedValue = (value) => {
  if (value === Infinity) return true;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "unlimited" || normalized === "infinity" || normalized === "unlimited reviewers") {
      return true;
    }
  }
  const numericValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numericValue) && numericValue < 0;
};

const formatMetricValue = (value) => {
  if (isFiniteMetric(value)) {
    const rounded = Math.round(value * 10) / 10;
    return String(rounded);
  }
  return hasDisplayValue(value) ? String(value) : UNKNOWN_VALUE;
};

const formatReviewerUsageText = (reviewerUsage = {}) => {
  const hasCurrent = hasDisplayValue(reviewerUsage.current);
  const hasLimit = hasDisplayValue(reviewerUsage.limit);

  if (hasCurrent && isUnlimitedValue(reviewerUsage.limit)) {
    return `${formatMetricValue(reviewerUsage.current)} used this month (Unlimited)`;
  }
  if (hasCurrent && hasLimit) {
    return `${formatMetricValue(reviewerUsage.current)} of ${formatMetricValue(reviewerUsage.limit)} used this month`;
  }
  if (hasCurrent) {
    return `${formatMetricValue(reviewerUsage.current)} used this month`;
  }
  if (isUnlimitedValue(reviewerUsage.limit)) {
    return "Unlimited this month";
  }
  if (hasLimit) {
    return `Limit: ${formatMetricValue(reviewerUsage.limit)} this month`;
  }
  return "Usage data unavailable";
};

const formatUsageRenewalDate = (dateValue) => {
  if (!hasDisplayValue(dateValue)) return null;
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

const getBillingRenewalText = (workspace = {}, i18nModule) => {
  const plan = workspace.plan || {};
  const usage = workspace.usage || {};
  if (plan.isLifetime && !plan.hasStripeSubscription) return null;
  const cancelAtPeriodEnd = plan.cancelAtPeriodEnd === true;
  const renewalDate = formatUsageRenewalDate(
    (cancelAtPeriodEnd && plan.cancellationDate) ||
    plan.renewalDate ||
    plan.periodEnd ||
    usage.renewalDate ||
    usage.periodEnd
  );
  if (cancelAtPeriodEnd) {
    const cancellationLabel = t(i18nModule, "YOUR_PLAN_WILL_BE_CANCELLED_ON");
    return renewalDate ? `${cancellationLabel} ${renewalDate}` : cancellationLabel;
  }
  return renewalDate ? `Renews ${renewalDate}` : null;
};

/* ================================================================
   Sidebar
   ================================================================ */
const shouldShowWorkspaceGroup = (workspace) => {
  if (!workspace) return false;
  // Personal workspaces always show the workspace group so the Members
  // section + invite tooltip flow can render regardless of plan tier.
  if (workspace.isPersonal) return true;
  // For non-personal workspaces the group is always shown (plan tier does
  // not gate visibility for team/shared workspaces).
  return true;
};

const Sidebar = ({ activeSection, onSectionChange, workspace, userRole, i18nModule, onUpgradePlan }) => {
  const showWorkspaceGroup = shouldShowWorkspaceGroup(workspace);
  const isPersonal = !!(workspace && workspace.isPersonal);
  const wsNavItems = WS_NAV_ITEMS_ALL.filter(
    (item) =>
      (!item.roles || item.roles.includes(userRole)) &&
      !(isPersonal && item.hideWhenPersonal)
  );

  return (
    <div className="settings-sidebar">
      {/* Account group */}
      <div className="settings-sb-group">
        <div className="settings-sb-group-label">{t(i18nModule, "ACCOUNT")}</div>
        {ACCOUNT_NAV_ITEMS.map((item) => (
          <button
            key={item.key}
            className={"settings-sb-item" + (activeSection === item.key ? " active" : "")}
            onClick={() => onSectionChange(item.key)}
          >
            {item.IconComponent ? (
              <item.IconComponent className="settings-sb-icon settings-sb-icon-mcp" />
            ) : (
              <span className={"settings-sb-icon " + item.icon}></span>
            )}
            {t(i18nModule, item.i18nKey) || item.fallback}
          </button>
        ))}
      </div>

      {showWorkspaceGroup ? (
        <div className="settings-sb-group">
          <div className="settings-sb-group-label" style={{ paddingTop: "var(--space-md)" }}>
            {t(i18nModule, "WORKSPACE_LABEL") || "Workspace"}
          </div>
          {wsNavItems.map((item) => (
            <button
              key={item.key}
              className={"settings-sb-item" + (activeSection === item.key ? " active" : "")}
              onClick={() => onSectionChange(item.key)}
            >
              <span className={"settings-sb-icon " + item.icon}></span>
              {t(i18nModule, item.i18nKey) || item.fallback}
            </button>
          ))}
        </div>
      ) : onUpgradePlan ? (
        <div className="settings-sb-group">
          <button className="settings-sb-item settings-sb-upgrade" onClick={onUpgradePlan}>
            <span className="settings-sb-icon pe-7s-rocket"></span>
            {t(i18nModule, "UPGRADE_PLAN") || "Upgrade Plan"}
          </button>
        </div>
      ) : null}

      <div className="settings-sb-spacer" />
    </div>
  );
};

/* ================================================================
   Profile Panel
   ================================================================ */
const ProfilePanel = ({ userInfo, onEditProfile, onChangeProfilePicture, i18nModule }) => {
  const username = userInfo && userInfo.getUsername ? userInfo.getUsername() : "";
  const email = userInfo && userInfo.getEmail ? userInfo.getEmail() : "";
  const profileImageUrl = userInfo && userInfo.getProfileImageUrl ? userInfo.getProfileImageUrl() : null;
  const hasProfileImage = profileImageUrl && profileImageUrl !== "none";

  return (
    <div>
      <div className="settings-ct-title">{t(i18nModule, "PROFILE")}</div>
      <div className="settings-ct-desc">{t(i18nModule, "MANAGE_PERSONAL_INFO")}</div>

      <div className="settings-card">
        <div className="settings-row">
          <div className="settings-profile-avatar">
            {hasProfileImage ? <img src={profileImageUrl} alt={username} /> : getInitials(username)}
          </div>
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "PROFILE_PHOTO")}</div>
            <div className="settings-row-desc">{t(i18nModule, "UPLOAD_PHOTO_DESC")}</div>
          </div>
          <TrebbleButton onClick={onChangeProfilePicture}>
            {t(i18nModule, "CHANGE_PROFILE_PICTURE")}
          </TrebbleButton>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "NAME_LABEL")}</div>
            <div className="settings-row-desc">{username || "--"}</div>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "EMAIL_LABEL")}</div>
            <div className="settings-row-desc">{email || "--"}</div>
          </div>
        </div>
      </div>

      <TrebbleButton type="primary" onClick={onEditProfile}>
        {t(i18nModule, "EDIT_PROFILE")}
      </TrebbleButton>
    </div>
  );
};

/* ================================================================
   Security Panel
   ================================================================ */
const SecurityPanel = ({ onChangePassword, i18nModule }) => {
  return (
    <div>
      <div className="settings-ct-title">{t(i18nModule, "SECURITY")}</div>
      <div className="settings-ct-desc">{t(i18nModule, "MANAGE_SECURITY_DESC")}</div>

      <div className="settings-card">
        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "PASSWORD_LABEL")}</div>
            <div className="settings-row-desc">{t(i18nModule, "CHANGE_PASSWORD_DESC")}</div>
          </div>
          <TrebbleButton onClick={onChangePassword}>
            {t(i18nModule, "CHANGE_PASSOWRD")}
          </TrebbleButton>
        </div>

        <div className="settings-row" style={{ opacity: 0.5 }}>
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "TWO_FACTOR_AUTH")}</div>
            <div className="settings-row-desc">{t(i18nModule, "TWO_FACTOR_DESC")}</div>
          </div>
          <span className="settings-badge settings-badge-gray">{t(i18nModule, "COMING_SOON")}</span>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   Notifications Panel
   ================================================================ */
const NotificationsPanel = ({
  pushNotificationsEnabled,
  emailNotificationsEnabled,
  onTogglePushNotifications,
  onToggleEmailNotifications,
  i18nModule,
}) => {
  return (
    <div>
      <div className="settings-ct-title">{t(i18nModule, "NOTIFICATIONS")}</div>
      <div className="settings-ct-desc">{t(i18nModule, "CONTROL_NOTIFICATIONS_DESC")}</div>

      <div className="settings-card">
        <div className="settings-section-label">{t(i18nModule, "PUSH_NOTIFICATIONS")}</div>
        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "PUSH_NOTIFICATION_FEED_UPDATE")}</div>
            <div className="settings-row-desc">{t(i18nModule, "TELL_ME_WHEN_MY_FEED_IS_READY_VIA_PUSH")}</div>
          </div>
          <TrebbleSwitch checked={pushNotificationsEnabled} onChange={(checked) => onTogglePushNotifications(checked)} />
        </div>

        <div className="settings-section-label" style={{ marginTop: "var(--text-base-px)" }}>{t(i18nModule, "EMAIL_FEED_UPDATE")}</div>
        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "EMAIL_FEED_UPDATE")}</div>
            <div className="settings-row-desc">{t(i18nModule, "TELL_ME_WHEN_MY_FEED_IS_READY_VIA_EMAIL")}</div>
          </div>
          <TrebbleSwitch checked={emailNotificationsEnabled} onChange={(checked) => onToggleEmailNotifications(checked)} />
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   Connections Panel
   ================================================================ */
const McpConnectionPanel = ({ i18nModule, useEditorTheme = false }) => {
  const [copiedKey, setCopiedKey] = useState(null);
  const mcpServerUrl = useMemo(() => getMcpServerUrl(), []);
  const oauthMetadataUrl = useMemo(() => getMcpOAuthMetadataUrl(), []);

  const handleCopy = useCallback((key, value) => {
    return copyTextToClipboard(value)
      .then(() => {
        setCopiedKey(key);
        if (window.alertSuccessMessage) {
          window.alertSuccessMessage(t(i18nModule, "MCP_COPIED"));
        }
        window.setTimeout(() => {
          setCopiedKey((currentKey) => currentKey === key ? null : currentKey);
        }, 1800);
      })
      .catch((error) => {
        console.error("Failed to copy MCP connection value:", error);
        if (window.alertErrorMessage) {
          window.alertErrorMessage(t(i18nModule, "MCP_COPY_FAILED"));
        }
      });
  }, [i18nModule]);

  const renderCopyButton = (key, value) => (
    <TrebbleButton
      useEditorTheme={useEditorTheme}
      onClick={() => handleCopy(key, value)}
      aria-label={t(i18nModule, "COPY") || "Copy"}
    >
      <span className="pe-7s-copy-file settings-mcp-copy-icon"></span>
      {copiedKey === key ? t(i18nModule, "COPIED") : t(i18nModule, "COPY")}
    </TrebbleButton>
  );

  return (
    <div>
      <div className="settings-ct-title">{t(i18nModule, "CONNECTIONS")}</div>
      <div className="settings-ct-desc">{t(i18nModule, "MCP_CONNECTIONS_DESC")}</div>

      <div className="settings-card settings-mcp-connection">
        <div className="settings-mcp-header">
          <div className="settings-mcp-mark">
            <McpLogoIcon className="settings-mcp-mark-icon" />
          </div>
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "TREBBLE_MCP")}</div>
            <div className="settings-row-desc">{t(i18nModule, "TREBBLE_MCP_DESC")}</div>
          </div>
        </div>

        <div className="settings-mcp-copy-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "MCP_SERVER_URL")}</div>
            <div className="settings-mcp-code">{mcpServerUrl}</div>
          </div>
          {renderCopyButton(COPY_TARGETS.SERVER, mcpServerUrl)}
        </div>

        <div className="settings-mcp-copy-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "OAUTH_METADATA_URL")}</div>
            <div className="settings-mcp-code">{oauthMetadataUrl}</div>
          </div>
          {renderCopyButton(COPY_TARGETS.OAUTH, oauthMetadataUrl)}
        </div>

        <div className="settings-info-box settings-mcp-info-box">
          <span className="settings-sb-icon pe-7s-info settings-mcp-info-icon"></span>
          {t(i18nModule, "MCP_ACCESS_MODEL_DESC")}
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   Workspace: General Panel
   ================================================================ */
const WsLogoUploadRow = ({ workspace, isReadOnly, i18nModule }) => {
  const fileInputRef = useRef(null);
  const [logoUrl, setLogoUrl] = useState(workspace.logoUrl || null);
  const [uploading, setUploading] = useState(false);

  // Keep logoUrl in sync when the workspace prop changes (e.g. user switches workspaces)
  useEffect(() => {
    setLogoUrl(workspace.logoUrl || null);
  }, [workspace.workspaceId]);

  const handleFileSelected = useCallback(async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const TrebbleClientAPI = (await import("../../models/helper/TrebbleClientAPI")).default;
      const api = TrebbleClientAPI.getInstance();

      // 1. Get the signed upload URL
      const signatureResponse = await api.getSignatureForWorkspaceLogoUpload({
        fileName: file.name,
        workspaceId: workspace.workspaceId,
      });

      // 2. Upload the file to S3 using the pre-signed URL
      const uploadUrl = signatureResponse.uploadUrl || signatureResponse.url;
      const fileKey = signatureResponse.fileKey || signatureResponse.key;
      // Only accept baseUrl from the dedicated baseUrl property — falling
      // back to signatureResponse.url would point to the signed PUT URL
      // (with query params) and corrupt the persisted logo URL.
      const baseUrl = signatureResponse.baseUrl;
      const persistedFileUrl = signatureResponse.fileUrl;

      const uploadResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
        mode: "cors",
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed with status ${uploadResponse.status}`);
      }

      // 3. Build the final logo URL
      let finalLogoUrl;
      if (baseUrl && fileKey) {
        finalLogoUrl = baseUrl.endsWith("/") ? baseUrl + fileKey : baseUrl + "/" + fileKey;
      } else if (persistedFileUrl) {
        finalLogoUrl = persistedFileUrl;
      } else {
        finalLogoUrl = uploadUrl.split("?")[0];
      }

      // 4. Tell the backend to persist the logo URL
      await api.updateWorkspaceLogo({
        workspaceId: workspace.workspaceId,
        logoUrl: finalLogoUrl,
      });

      setLogoUrl(finalLogoUrl);
      // Update workspace list so the sidebar switcher reflects the new logo
      try {
        const LocalStorageHelper = (await import("../../models/helper/LocalStorageHelper")).default;
        const workspaces = LocalStorageHelper.getInstance().getWorkspaceList() || [];
        const updated = workspaces.map(function(w) {
          return (w.workspaceId || w._id) === workspace.workspaceId ? Object.assign({}, w, { logoUrl: finalLogoUrl }) : w;
        });
        LocalStorageHelper.getInstance().setWorkspaceList(updated);
      } catch (_e) { /* best effort */ }
      if (window.alertSuccessMessage) {
        window.alertSuccessMessage(t(i18nModule, "WORKSPACE_LOGO_UPDATED"));
      }
    } catch (error) {
      console.error("Logo upload failed:", error);
      if (window.alertErrorMessage) {
        window.alertErrorMessage(t(i18nModule, "FAILED_UPLOAD_LOGO"), error.message || t(i18nModule, "UNKNOWN_ERROR"));
      } else if (navigator.trebbleNotification) {
        navigator.trebbleNotification.alert(error.message || t(i18nModule, "FAILED_UPLOAD_LOGO"), null, false, "error");
      }
    } finally {
      setUploading(false);
      // Reset file input so re-selecting the same file triggers onChange
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [workspace.workspaceId]);

  const handleRemoveLogo = useCallback(async () => {
    setUploading(true);
    try {
      const TrebbleClientAPI = (await import("../../models/helper/TrebbleClientAPI")).default;
      const api = TrebbleClientAPI.getInstance();

      await api.deleteWorkspaceLogo({ workspaceId: workspace.workspaceId });

      setLogoUrl(null);
      // Update workspace list so the sidebar switcher reflects logo removal
      try {
        const LocalStorageHelper = (await import("../../models/helper/LocalStorageHelper")).default;
        const workspaces = LocalStorageHelper.getInstance().getWorkspaceList() || [];
        const updated = workspaces.map(function(w) {
          return (w.workspaceId || w._id) === workspace.workspaceId ? Object.assign({}, w, { logoUrl: null }) : w;
        });
        LocalStorageHelper.getInstance().setWorkspaceList(updated);
      } catch (_e) { /* best effort */ }
      if (window.alertSuccessMessage) {
        window.alertSuccessMessage(t(i18nModule, "WORKSPACE_LOGO_REMOVED"));
      }
    } catch (error) {
      console.error("Logo removal failed:", error);
      if (window.alertErrorMessage) {
        window.alertErrorMessage(t(i18nModule, "FAILED_REMOVE_LOGO"), error.message || t(i18nModule, "UNKNOWN_ERROR"));
      } else if (navigator.trebbleNotification) {
        navigator.trebbleNotification.alert(error.message || t(i18nModule, "FAILED_REMOVE_LOGO"), null, false, "error");
      }
    } finally {
      setUploading(false);
    }
  }, [workspace.workspaceId]);

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const initial = workspace.workspaceName ? workspace.workspaceName.trim().charAt(0).toUpperCase() : "?";

  return (
    <div className="settings-row">
      <div className="settings-ws-logo-preview">
        {logoUrl ? (
          <img src={logoUrl} alt={workspace.workspaceName || "Logo"} />
        ) : (
          <span className="settings-ws-logo-placeholder pe-7s-photo" />
        )}
      </div>
      <div className="settings-row-info">
        <div className="settings-row-label">{t(i18nModule, "WORKSPACE_LOGO")}</div>
        <div className="settings-row-desc">
          {logoUrl ? t(i18nModule, "CHANGE_OR_REMOVE_LOGO") : t(i18nModule, "UPLOAD_LOGO_DESC")}
        </div>
      </div>
      {!isReadOnly && (
        <div style={{ display: "flex", alignItems: "center", gap: "var(--border-6)" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileSelected}
          />
          <TrebbleButton onClick={handleUploadClick} disabled={uploading}>
            {uploading ? t(i18nModule, "UPLOADING") : (logoUrl ? t(i18nModule, "CHANGE_LABEL") : t(i18nModule, "UPLOAD_LOGO"))}
          </TrebbleButton>
          {logoUrl && (
            <TrebbleButton danger onClick={handleRemoveLogo} disabled={uploading}>
              {t(i18nModule, "REMOVE_LABEL")}
            </TrebbleButton>
          )}
        </div>
      )}
    </div>
  );
};

const WsGeneralPanel = ({ workspace, userRole, i18nModule, onLeaveWorkspace, onDeleteWorkspace }) => {
  const roleBadge = ROLE_BADGES[userRole] || ROLE_BADGES.editor;
  const isReadOnly = userRole !== "owner";
  // Default workspace (no explicit workspaceId): "Leave" and "Delete" don't
  // apply — there's nothing to leave (it's the user's own scope) and you
  // can't delete the default workspace, only your account itself.
  const isPersonal = !!(workspace && workspace.isPersonal);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const workspaceName = (workspace && workspace.workspaceName) || "";

  const handleDeleteClick = useCallback(() => {
    setShowDeleteConfirm(true);
    setDeleteConfirmName("");
  }, []);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteConfirmName("");
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteConfirmName !== workspaceName) return;
    setDeleting(true);
    // Treat onDeleteWorkspace as possibly returning a Promise so a backend
    // failure re-enables the UI instead of leaving deleting=true stuck.
    Promise.resolve()
      .then(() => onDeleteWorkspace && onDeleteWorkspace())
      .catch((err) => {
        console.error("Failed to delete workspace:", err);
        setDeleting(false);
      });
  }, [deleteConfirmName, workspaceName, onDeleteWorkspace]);

  return (
    <div>
      <div className="settings-ct-title">{t(i18nModule, "WS_GENERAL")}</div>
      <div className="settings-ct-desc">{t(i18nModule, "WS_GENERAL_DESC")}</div>

      <div className="settings-card">
        <WsLogoUploadRow workspace={workspace} isReadOnly={isReadOnly} i18nModule={i18nModule} />

        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "WORKSPACE_NAME")}</div>
            <div className="settings-row-desc">{workspace.workspaceName || "--"}</div>
          </div>
          {!isReadOnly && (
              <TrebbleTooltip title={t(i18nModule, "AVAILABLE_IN_NEXT_UPDATE")}>
                <span><TrebbleButton disabled>Edit</TrebbleButton></span>
              </TrebbleTooltip>
            )}
        </div>

        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">{t(i18nModule, "YOUR_ROLE")}</div>
            <div className="settings-row-desc">
              {userRole === "owner"
                ? t(i18nModule, "ROLE_OWNER_DESC")
                : userRole === "admin"
                ? t(i18nModule, "ROLE_ADMIN_DESC")
                : t(i18nModule, "ROLE_EDITOR_DESC")}
            </div>
          </div>
          <span
            className="settings-badge"
            style={{ background: roleBadge.color + "18", color: roleBadge.color }}
          >
            {roleBadge.label}
          </span>
        </div>

        {workspace.workspaceId && (
          <div className="settings-row">
            <div className="settings-row-info">
              <div className="settings-row-label">{t(i18nModule, "WORKSPACE_ID")}</div>
              <div className="settings-row-desc" style={{ fontFamily: "monospace", fontSize: "var(--text-xs-px)" }}>
                {workspace.workspaceId}
              </div>
            </div>
          </div>
        )}
      </div>

      {isReadOnly && (
        <div className="settings-info-box">
          <span className="settings-sb-icon pe-7s-info" style={{ marginRight: "var(--border-8)" }}></span>
          {t(i18nModule, "SETTINGS_READONLY_INFO")}
        </div>
      )}

      {isReadOnly && !isPersonal && onLeaveWorkspace && (
        <div className="settings-card" style={{ marginTop: "var(--space-lg)", background: "rgba(255, 59, 48, 0.08)", padding: "var(--space-md)" }}>
          <div className="settings-row">
            <div className="settings-row-info">
              <div className="settings-row-label">{t(i18nModule, "LEAVE_WORKSPACE")}</div>
              <div className="settings-row-desc">
                {t(i18nModule, "LEAVE_WORKSPACE_DESC")}
              </div>
            </div>
            <TrebbleButton danger onClick={onLeaveWorkspace}>
              {t(i18nModule, "LEAVE_WORKSPACE")}
            </TrebbleButton>
          </div>
        </div>
      )}

      {!isReadOnly && !isPersonal && onDeleteWorkspace && (
        <div style={{ marginTop: "var(--space-xl)", borderTop: "1px solid rgba(128,128,128,0.3)", paddingTop: "var(--space-lg)" }}>
          <div style={{ fontSize: "14px", fontWeight: 600, marginBottom: "var(--space-sm)", color: "#ff4d4f" }}>
            {t(i18nModule, "DANGER_ZONE") || "Danger Zone"}
          </div>
          <div style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "var(--space-md)" }}>
            {t(i18nModule, "DELETE_WORKSPACE_WARNING") || "Deleting this workspace will remove all member access. Projects will be preserved for 30 days."}
          </div>
          {!showDeleteConfirm && (
            <TrebbleButton
              danger
              onClick={handleDeleteClick}
              useEditorTheme={false}
              style={{ background: "transparent" }}
            >
              {t(i18nModule, "DELETE_WORKSPACE") || "Delete Workspace"}
            </TrebbleButton>
          )}
          {showDeleteConfirm && (
            <div className="settings-card" style={{ background: "rgba(255, 59, 48, 0.08)", padding: "var(--space-md)" }}>
              <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "var(--space-sm)" }}>
                {t(i18nModule, "DELETE_WORKSPACE_CONFIRM_TITLE") || "Delete workspace?"}
              </div>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "var(--space-sm)" }}>
                {(t(i18nModule, "DELETE_WORKSPACE_CONFIRM_PROMPT") || "Type the workspace name to confirm:")}
                {" "}
                <strong>{workspaceName}</strong>
              </div>
              <TrebbleInput
                value={deleteConfirmName}
                onChange={function(e) { setDeleteConfirmName(e.target.value); }}
                placeholder={workspaceName}
                useEditorTheme={false}
                style={{ marginBottom: "var(--space-md)", width: "100%" }}
                disabled={deleting}
              />
              <div style={{ display: "flex", gap: "var(--space-sm)" }}>
                <TrebbleButton
                  onClick={handleCancelDelete}
                  useEditorTheme={false}
                  disabled={deleting}
                >
                  {t(i18nModule, "CANCEL") || "Cancel"}
                </TrebbleButton>
                <TrebbleButton
                  danger
                  onClick={handleConfirmDelete}
                  useEditorTheme={false}
                  disabled={deleteConfirmName !== workspaceName || deleting}
                >
                  {deleting ? "Deleting..." : (t(i18nModule, "DELETE_WORKSPACE") || "Delete Workspace")}
                </TrebbleButton>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ================================================================
   Workspace: Members Panel
   ================================================================ */
const WsMembersPanel = ({ workspace, userRole, members, reviewers, onInviteMember, onRemoveMember, onRemoveReviewer, onSetMemberRole }) => {
  const [memberListMode, setMemberListMode] = useState("editors");
  const canManageMembers = userRole === "owner" || userRole === "admin";
  const reviewerUsage = workspace.reviewerUsage || {};
  // Gate the Invite button on an actual paid entitlement (Stripe subscription
  // OR AppSumo lifetime credit) — NOT just `planType !== "free"`. The
  // synthesized default workspace also reports `planType: "custom"` for
  // non-lifetime promo / gift / partner credits (`isLifetime: false`) that
  // explicitly keep the user at Free-tier storage and editor limits, so a
  // planType-only check would let them through and silently start the
  // materialization flow. Mirror the hour-pack gate in WsUsagePanel (gate
  // var + tooltip wrapped around a <span> so the tooltip still fires on a
  // disabled button).
  const hasStripeSubscription = !!(workspace.plan && workspace.plan.hasStripeSubscription);
  const isLifetime = !!(workspace.plan && workspace.plan.isLifetime);
  const hasPaidEntitlement = hasStripeSubscription || isLifetime;
  const inviteMemberDisabled = !canManageMembers || !hasPaidEntitlement || !onInviteMember;
  const inviteMemberTooltip = !hasPaidEntitlement
    ? "Upgrade to a paid plan to invite members."
    : !canManageMembers
    ? "Only workspace owners and admins can invite members"
    : "";

  const MEMBER_ROLE_OPTIONS = [
    { value: "editor", label: "Editor" },
    { value: "reviewer", label: "Reviewer" },
    { value: "admin", label: "Admin" },
  ];

  const allMembers = useMemo(() => (members || []).map(function(member) {
    return {
      ...member,
      profileImageUrl: normalizeProfileImageUrlForHttps(member.profileImageUrl),
    };
  }), [members]);
  const allReviewers = useMemo(() => (reviewers || []).map(function(reviewer) {
    return {
      ...reviewer,
      profileImageUrl: normalizeProfileImageUrlForHttps(reviewer.profileImageUrl),
    };
  }), [reviewers]);
  const activeMembers = allMembers.filter(function(m) { return m.role !== "pending" && m.status !== "invited" && m.status !== "pending"; });
  const pendingMembers = allMembers.filter(function(m) { return m.status === "invited" || m.status === "pending"; });

  const renderMemberRow = function(member, idx) {
    const badge = ROLE_BADGES[member.role] || ROLE_BADGES.editor;
    const isPending = member.status === "invited" || member.status === "pending";
    const isOwnerRow = member.role === "owner";
    const isViewerRow = member.role === "viewer";
    const isYouRow = member.isYou;
    const showControls = canManageMembers && !isOwnerRow && !isYouRow && !isViewerRow;
    const memberProfileImageUrl = member.profileImageUrl;

    return (
      <div className="settings-row" key={member.userId || member.memberRecordId || idx}>
        <div className="settings-profile-avatar" style={{ width: 36, height: 36, fontSize: "var(--text-sm-px)" }}>
          {memberProfileImageUrl ? (
            <img src={memberProfileImageUrl} alt={member.name || member.email} />
          ) : (
            getInitials(member.name || member.email || "?")
          )}
        </div>
        <div className="settings-row-info">
          <div className="settings-row-label">
            {member.name || member.email}
            {member.isYou && <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> (you)</span>}
          </div>
          {member.name && member.email && (
            <div className="settings-row-desc">{member.email}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--border-8)" }}>
          {isPending ? (
            <span className="settings-badge settings-badge-gray">Pending</span>
          ) : showControls && onSetMemberRole ? (
            // Use an Ant Design Dropdown with a plain <button> trigger. Prior
            // rounds tried a native <select data-role="none"> but Playwright's
            // visibility check kept flagging it as hidden in the headless run
            // (the trace shows computed box=0 despite min-width/min-height —
            // likely inherited -webkit-appearance:none from a jQM-era reset
            // leaving the native widget without a layout box). A button is
            // bulletproof: it lays out as an inline-block, has a visible
            // accessible name via aria-label, and TrebbleDropdown renders the
            // role options on click. Keyboard + screen-reader behaviour is
            // identical to the old <select>.
            (function() {
              var currentRoleValue = member.role === "member" ? "editor" : member.role;
              var currentOpt = MEMBER_ROLE_OPTIONS.find(function(opt) { return opt.value === currentRoleValue; })
                || MEMBER_ROLE_OPTIONS[0];
              var menuItems = MEMBER_ROLE_OPTIONS.map(function(opt) {
                return {
                  key: opt.value,
                  label: opt.label,
                  onClick: function() {
                    onSetMemberRole(member.memberRecordId || member.userId, opt.value);
                  }
                };
              });
              return (
                <TrebbleDropdown
                  useEditorTheme={false}
                  menu={{ items: menuItems, selectable: true, selectedKeys: [currentOpt.value] }}
                  trigger={["click"]}
                >
                  <button
                    type="button"
                    className="settings-role-select"
                    aria-label={"Role for " + (member.name || member.email)}
                    style={{
                      fontSize: "var(--text-xs-px)",
                      padding: "4px 8px",
                      minWidth: 84,
                      minHeight: 24,
                      border: "1px solid var(--border-color, #d0d0d0)",
                      background: "transparent",
                      color: "inherit",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "var(--border-4)",
                    }}
                  >
                    <span>{currentOpt.label}</span>
                    <span aria-hidden="true" style={{ fontSize: "10px", lineHeight: 1 }}>▾</span>
                  </button>
                </TrebbleDropdown>
              );
            })()
          ) : (
            <span
              className="settings-badge"
              style={{ background: badge.color + "18", color: badge.color }}
            >
              {badge.label}
            </span>
          )}
          {showControls && onRemoveMember ? (
            <TrebbleButton
              aria-label={"Remove " + (member.name || member.email)}
              type="text"
              danger
              size="small"
              onClick={function() { onRemoveMember(member.memberRecordId || member.userId); }}
              style={{ minWidth: 0, padding: "0 4px" }}
            >
              <span style={{ fontSize: "16px", lineHeight: 1 }}>&#x2715;</span>
            </TrebbleButton>
          ) : (
            <span style={{ display: "inline-block", width: 24 }}></span>
          )}
        </div>
      </div>
    );
  };

  const renderReviewerRow = function(reviewer, idx) {
    const projectCount = reviewer.activeProjectCount || (reviewer.projects ? reviewer.projects.length : 0) || 0;
    const pendingCount = reviewer.pendingInviteCount || 0;
    const accessSummary = projectCount > 0
      ? projectCount + " project" + (projectCount !== 1 ? "s" : "")
      : pendingCount > 0
      ? pendingCount + " pending invite" + (pendingCount !== 1 ? "s" : "")
      : "No active project access";
    const displayName = reviewer.name || reviewer.email;
    const shouldShowEmail = reviewer.email && reviewer.email !== displayName;
    const reviewerProfileImageUrl = reviewer.profileImageUrl;

    return (
      <div
        className="settings-row"
        key={reviewer.reviewerId || reviewer.email || reviewer.userUID || idx}
        style={{ alignItems: "flex-start", paddingTop: "var(--border-10)", paddingBottom: "var(--border-10)" }}
      >
        <div className="settings-profile-avatar" style={{ width: 36, height: 36, fontSize: "var(--text-sm-px)" }}>
          {reviewerProfileImageUrl ? (
            <img src={reviewerProfileImageUrl} alt={reviewer.name || reviewer.email} />
          ) : (
            getInitials(reviewer.name || reviewer.email || "?")
          )}
        </div>
        <div className="settings-row-info">
          <div className="settings-row-label" style={{ marginBottom: "2px" }}>
            {displayName}
          </div>
          {shouldShowEmail && (
            <div className="settings-row-desc" style={{ marginBottom: "2px" }}>
              {reviewer.email}
            </div>
          )}
          <div className="settings-row-desc">{accessSummary}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--border-8)", paddingTop: "2px" }}>
          {reviewer.status !== "active" && (
            <span className="settings-badge settings-badge-gray">Pending</span>
          )}
          {canManageMembers && onRemoveReviewer ? (
            <TrebbleButton
              aria-label={"Remove reviewer " + (reviewer.name || reviewer.email)}
              type="text"
              danger
              size="small"
              onClick={function() { onRemoveReviewer(reviewer); }}
              style={{ minWidth: 0, padding: "0 4px" }}
            >
              <span style={{ fontSize: "16px", lineHeight: 1 }}>&#x2715;</span>
            </TrebbleButton>
          ) : (
            <span style={{ display: "inline-block", width: 24 }}></span>
          )}
        </div>
      </div>
    );
  };

  const renderEditorsList = function() {
    return (
      <React.Fragment>
        <div className="settings-card">
          <div className="settings-section-label">
            {activeMembers.length} member{activeMembers.length !== 1 ? "s" : ""}
          </div>
          {activeMembers.map(renderMemberRow)}
        </div>

        {pendingMembers.length > 0 && (
          <div className="settings-card" style={{ marginTop: "var(--text-base-px)" }}>
            <div className="settings-section-label">
              {pendingMembers.length} pending invite{pendingMembers.length !== 1 ? "s" : ""}
            </div>
            {pendingMembers.map(renderMemberRow)}
          </div>
        )}

      </React.Fragment>
    );
  };

  const renderReviewersList = function() {
    const activeReviewers = allReviewers.filter(function(reviewer) {
      return reviewer.status === "active" || reviewer.activeProjectCount > 0;
    });
    const pendingReviewers = allReviewers.filter(function(reviewer) {
      return activeReviewers.indexOf(reviewer) === -1;
    });

    return (
      <React.Fragment>
        <div className="settings-card">
          <div className="settings-section-label">
            {activeReviewers.length} active reviewer{activeReviewers.length !== 1 ? "s" : ""}
          </div>
          {activeReviewers.length > 0 ? (
            activeReviewers.map(renderReviewerRow)
          ) : (
            <div className="settings-row-desc" style={{ padding: "var(--text-base-px) 0" }}>
              No active project reviewers yet.
            </div>
          )}
        </div>
        {pendingReviewers.length > 0 && (
          <div className="settings-card" style={{ marginTop: "var(--text-base-px)" }}>
            <div className="settings-section-label">
              {pendingReviewers.length} pending reviewer invite{pendingReviewers.length !== 1 ? "s" : ""}
            </div>
            {pendingReviewers.map(renderReviewerRow)}
          </div>
        )}
      </React.Fragment>
    );
  };

  return (
    <div>
      <div className="settings-ct-title">Members</div>
      <div className="settings-ct-desc">
        {canManageMembers
          ? "Manage who has access to this workspace"
          : "View workspace members"}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--text-base-px)",
          margin: "var(--text-base-px) 0",
        }}
      >
        <TrebbleSegmented
          aria-label="Member list type"
          value={memberListMode}
          onChange={function(value) { setMemberListMode(value); }}
          options={[
            { label: <span>Editors</span>, value: "editors" },
            { label: <span>Reviewers</span>, value: "reviewers" },
          ]}
        />
        {canManageMembers && memberListMode === "editors" && (() => {
          const inviteMemberButton = (
            <TrebbleButton type="primary" onClick={onInviteMember} disabled={inviteMemberDisabled}>
              <span className="pe-7s-add-user" style={{ marginRight: "var(--border-6)", WebkitTextStroke: "0.5px" }}></span>
              Invite Member
            </TrebbleButton>
          );
          return inviteMemberTooltip ? (
            <TrebbleTooltip title={inviteMemberTooltip}>
              <span>{inviteMemberButton}</span>
            </TrebbleTooltip>
          ) : inviteMemberButton;
        })()}
      </div>

      {memberListMode === "reviewers" ? renderReviewersList() : renderEditorsList()}

      {/* Reviewer usage summary */}
      <div className="settings-info-box">
        <span className="settings-sb-icon pe-7s-info" style={{ marginRight: "var(--border-8)" }}></span>
        Reviewer slots: {formatReviewerUsageText(reviewerUsage)}.
        Reviewers are invited per-project from the project sharing settings. Removing a reviewer revokes access now; this month&apos;s usage resets on renewal.
      </div>
    </div>
  );
};

/* ================================================================
   Workspace: Billing Panel (Owner + Admin only)
   ================================================================ */
const WsBillingPanel = ({ workspace, userRole, i18nModule, onUpgradePlan, onUpdatePaymentMethod, onChangePlan, onCancelSubscription }) => {
  const isOwner = userRole === "owner";
  const plan = (workspace && workspace.plan) || {};
  const planType = (plan.planType || "free").toLowerCase();
  const isFreePlan = planType === "free";
  const isCustomPlan = planType === "custom";
  // Stripe-backed = there's an active stripe creator/workspace subscription
  // we can change or cancel through Stripe. Lifetime-only (AppSumo) has
  // `isLifetime` true but no stripe sub to cancel.
  const hasStripeSubscription = !!plan.hasStripeSubscription;
  const subscriptionCancellationScheduled = hasStripeSubscription && plan.cancelAtPeriodEnd === true;
  const isLifetimeOnly = !!plan.isLifetime && !hasStripeSubscription;
  const billingRenewalText = getBillingRenewalText(workspace, i18nModule);
  const planFeatureItems = [
    hasDisplayValue(plan.hours) ? `${plan.hours} hr/month` : "Hours vary",
    hasDisplayValue(plan.storage) ? `${plan.storage} storage` : "Storage varies",
    hasDisplayValue(plan.editors) ? `${plan.editors} editors` : "Seat limit varies",
    hasDisplayValue(plan.reviewerLabel) ? plan.reviewerLabel : "Reviewer limit varies",
  ];
  if (billingRenewalText) {
    planFeatureItems.push(billingRenewalText);
  }
  const planFeatures = planFeatureItems.join(" · ");
  const planPrice = hasDisplayValue(plan.price) ? plan.price : (isFreePlan ? "Free" : "Custom");
  const planPricePeriod = hasDisplayValue(plan.pricePeriod) ? plan.pricePeriod : null;

  // Pick the primary CTA copy + handler based on plan state.
  // Free → "Upgrade Plan" (uses onUpgradePlan)
  // Paid (stripe or lifetime) → "Change Plan" (uses onChangePlan)
  const primaryCtaLabel = isFreePlan ? (t(i18nModule, "UPGRADE_PLAN") || "Upgrade Plan") : "Change Plan";
  const primaryCtaIcon = isFreePlan ? "pe-7s-rocket" : "pe-7s-refresh-2";
  const primaryCtaHandler = isFreePlan ? onUpgradePlan : onChangePlan;

  return (
    <div>
      <div className="settings-ct-title">Billing</div>
      <div className="settings-ct-desc">
        {isOwner ? "Manage your subscription and payment" : "View subscription details"}
      </div>

      {!isOwner && (
        <div className="settings-info-box" style={{ marginBottom: "var(--text-base-px)" }}>
          <span className="settings-sb-icon pe-7s-info" style={{ marginRight: "var(--border-8)" }}></span>
          Billing is managed by the workspace owner.
        </div>
      )}

      {/* Current plan */}
      <div className="settings-section">
        <div className="settings-section-label">Current Plan</div>
        <div className="settings-plan-card">
          <div className="settings-plan-name">{hasDisplayValue(plan.name) ? plan.name : t(i18nModule, "WORKSPACE_PLAN")}</div>
          <div className="settings-plan-price">
            {planPrice}
            {planPricePeriod && <span className="settings-plan-period">{planPricePeriod}</span>}
          </div>
          <div className="settings-plan-features">{planFeatures}</div>
          {/* Combined Stripe + AppSumo lifetime: explain the tally so the user
              understands "Custom" means subscription + lifetime-credit. Only
              shown when BOTH a Stripe subscription AND a lifetime credit are
              active — lifetime-only users are also labeled "Custom" but for
              them the note below ("Lifetime credit from AppSumo…") is the
              right copy. */}
          {isCustomPlan && hasStripeSubscription && (
            <div className="settings-plan-note" style={{ marginTop: "var(--space-sm)", fontSize: "var(--text-xs-px)", opacity: 0.8 }}>
              Combined: your subscription plan plus your AppSumo lifetime credit. Hours and limits are tallied across both.
            </div>
          )}
        </div>
        {isOwner && primaryCtaHandler && (
          <div style={{ marginTop: "var(--text-xs-px)", display: "flex", gap: "var(--border-8)", flexWrap: "wrap" }}>
            <TrebbleButton type="primary" onClick={primaryCtaHandler}>
              <span className={primaryCtaIcon} style={{ marginRight: "var(--border-6)", WebkitTextStroke: "0.5px" }}></span>
              {primaryCtaLabel}
            </TrebbleButton>
            {/* Cancel only when there's an actual stripe subscription. AppSumo
                lifetime can't be cancelled (it's a one-time redeem). */}
            {hasStripeSubscription && !subscriptionCancellationScheduled && onCancelSubscription && (
              <TrebbleButton danger onClick={onCancelSubscription} style={{ background: "transparent" }}>
                Cancel Subscription
              </TrebbleButton>
            )}
          </div>
        )}
        {isOwner && isLifetimeOnly && (
          <div className="settings-info-box" style={{ marginTop: "var(--space-md)" }}>
            <span className="settings-sb-icon pe-7s-info" style={{ marginRight: "var(--border-8)" }}></span>
            Lifetime credit from AppSumo. There&apos;s nothing to cancel — your hours don&apos;t expire.
          </div>
        )}
      </div>

      {/* Payment method — show when the owner either has a stripe sub OR
          has no card on file yet (so the "Add Payment Method" CTA is
          always reachable for owners). */}
      {isOwner && (hasStripeSubscription || !plan.hasPaymentMethod) && (
        <div className="settings-section">
          <div className="settings-section-label">Payment Method</div>
          <div className="settings-card">
            <div className="settings-row">
              <div className="settings-row-info">
                <div className="settings-row-label">{plan.paymentMethod || "No payment method on file"}</div>
                <div className="settings-row-desc">{plan.paymentExpiry || "Add a payment method to manage your subscription"}</div>
              </div>
              {/* Label mirrors the real billing state: "Add Payment Method"
                  when no card is on file (Stripe Checkout setup mode),
                  "Update" when one exists (Stripe Customer Portal). The
                  onClick handler branches on the same flag. */}
              <TrebbleButton onClick={onUpdatePaymentMethod}>
                {plan.hasPaymentMethod ? "Update" : "Add Payment Method"}
              </TrebbleButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ================================================================
   Workspace: Usage Panel
   ================================================================ */
const UsageBar = ({ label, used, total, unit }) => {
  const hasUsed = isFiniteMetric(used);
  const hasTotal = isFiniteMetric(total);
  const percentage = hasUsed && hasTotal && total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const isWarning = percentage >= 75;
  const isDanger = percentage >= 90;

  return (
    <div className="settings-section">
      <div className="settings-row">
        <div className="settings-row-info" style={{ flex: 1 }}>
          <div className="settings-row-label">{label}</div>
          <div className="settings-usage-bar-track">
            <div
              className={
                "settings-usage-bar-fill" +
                (isDanger ? " danger" : isWarning ? " warning" : "")
              }
              style={{ width: percentage + "%" }}
            />
          </div>
          <div className="settings-row-desc">
            {formatMetricValue(used)} / {formatMetricValue(total)} {unit}
          </div>
        </div>
      </div>
    </div>
  );
};

const WsUsagePanel = ({ workspace, userRole, members, onPurchaseHourPack }) => {
  const usage = workspace.usage || {};
  const reviewerUsage = workspace.reviewerUsage || {};
  const planType = (workspace.plan && workspace.plan.planType) || "free";
  const hasStripeSubscription = !!(workspace.plan && workspace.plan.hasStripeSubscription);
  // Hour packs are billed against the customer's Stripe subscription
  // (api/v1/workspace/billing/purchaseHourPack creates a one-off
  // invoice on the existing Subscription, then the webhook credits
  // hours back). Two states pass `planType !== "free"` but have no
  // way to charge — both used to render the button enabled and silent-
  // no-op on click:
  //   1. AppSumo lifetime users (`planType: "custom"`) on the
  //      synthesized default workspace — `hasStripeSubscription: false`.
  //   2. A workspace doc that was upgraded but the Stripe sub got
  //      cancelled / scrubbed (e.g. cloned-from-prod accounts where
  //      we null the live Stripe IDs).
  // Gate on `hasStripeSubscription` so both cases land in the
  // disabled-with-tooltip branch instead. Owners on a real paid plan
  // still see the button enabled and clickable.
  const isPaidPlanWithSubscription = planType !== "free" && hasStripeSubscription;
  // Owners and admins can spend money on add-ons; everyone else sees the
  // button disabled (per project rule: "Disable vs Hide — always disable
  // with a tooltip").
  const canManageBilling = userRole === "owner" || userRole === "admin";
  const buyMoreHoursDisabled = !isPaidPlanWithSubscription || !canManageBilling || !onPurchaseHourPack;
  // Tooltip priority: explain the cheapest blocker first. "Upgrade your
  // plan" reaches more users than "you need a card on file" — the latter
  // only fires for the narrow case of a paid sub that lost its payment
  // method, while the former covers free + AppSumo + sub-cancelled.
  const buyMoreHoursTooltip = planType === "free"
    ? "Upgrade to a paid plan to buy hour packs"
    : !hasStripeSubscription
    ? "Add a payment method to buy hour packs"
    : !canManageBilling
    ? "Only workspace owners and admins can purchase add-ons"
    : "";

  const buyMoreHoursButton = (
    <TrebbleButton
      type="primary"
      onClick={onPurchaseHourPack}
      disabled={buyMoreHoursDisabled}
    >
      <span className="pe-7s-clock" style={{ marginRight: "var(--border-6)", WebkitTextStroke: "0.5px" }}></span>
      Buy More Hours
    </TrebbleButton>
  );

  return (
    <div>
      <div className="settings-ct-title">Usage</div>
      <div className="settings-ct-desc">Resource consumption for this billing cycle</div>

      <UsageBar
        label="Transcription Hours"
        used={usage.hoursUsed}
        total={usage.hoursTotal}
        unit="hr"
      />

      <div style={{ marginTop: "8px", marginBottom: "16px", display: "flex", alignItems: "center" }}>
        {buyMoreHoursTooltip ? (
          <TrebbleTooltip title={buyMoreHoursTooltip}>
            <span>{buyMoreHoursButton}</span>
          </TrebbleTooltip>
        ) : (
          buyMoreHoursButton
        )}
      </div>

      <UsageBar
        label="Storage"
        used={usage.storageUsedGB}
        total={usage.storageTotalGB}
        unit="GB"
      />

      <UsageBar
        label="Editor Seats"
        used={usage.editorsUsed}
        total={usage.editorsTotal}
        unit="seats"
      />

      {/* Per-editor breakdown (owner/admin only) */}
      {(userRole === "owner" || userRole === "admin") && members && members.length > 0 && (
        <div className="settings-section">
          <div className="settings-section-label">Per-Editor Breakdown</div>
          {members
            .filter((m) => m.role === "owner" || m.role === "editor" || m.role === "admin")
            .map((member, idx) => (
              <div className="settings-row" key={member.userId || idx}>
                <div className="settings-profile-avatar" style={{ width: 28, height: 28, fontSize: "var(--text-xs-px)" }}>
                  {getInitials(member.name)}
                </div>
                <div className="settings-row-info">
                  <div className="settings-row-label">{member.name}</div>
                </div>
                <div className="settings-row-value">
                  {formatMetricValue(member.hoursUsed)} hr &middot; {formatMetricValue(member.storageUsedGB)} GB
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Reviewer usage */}
      <div className="settings-section">
        <div className="settings-section-label">Reviewer Slots</div>
        <div className="settings-row">
          <div className="settings-row-info">
            <div className="settings-row-label">
              {formatReviewerUsageText(reviewerUsage)}
            </div>
            <div className="settings-row-desc">Reviewers are invited per-project</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   Main SettingsPage Component
   ================================================================ */
const SettingsPage = ({
  userInfo,
  workspace,
  userRole,
  members,
  reviewers,
  onEditProfile,
  onChangePassword,
  onChangeProfilePicture,
  onLogout,
  onTogglePushNotifications,
  onToggleEmailNotifications,
  onInviteMember,
  onRemoveMember,
  onRemoveReviewer,
  onLeaveWorkspace,
  onDeleteWorkspace,
  onSetMemberRole,
  onUpgradePlan,
  onUpdatePaymentMethod,
  onChangePlan,
  onCancelSubscription,
  onPurchaseHourPack,
  initialPushNotificationsEnabled,
  initialEmailNotificationsEnabled,
  i18nModule,
  controlledSection,
}) => {
  const [activeSection, setActiveSection] = useState(SECTION_PROFILE);

  useEffect(() => {
    if (controlledSection && VALID_SECTIONS.has(controlledSection)) {
      setActiveSection(controlledSection);
    }
  }, [controlledSection]);

  useEffect(() => {
    const isWorkspaceSection =
      activeSection === SECTION_WS_GENERAL ||
      activeSection === SECTION_WS_MEMBERS ||
      activeSection === SECTION_WS_BILLING ||
      activeSection === SECTION_WS_USAGE;

    if (!isWorkspaceSection) return;

    // Role + entitlement enforcement: bounce to Profile if the current user
    // should not see workspace settings in this context.
    if (!shouldShowWorkspaceGroup(workspace)) {
      setActiveSection(SECTION_PROFILE);
      return;
    }
    const isPersonal = !!(workspace && workspace.isPersonal);
    const canAccess = WS_NAV_ITEMS_ALL.some(
      (item) =>
        item.key === activeSection &&
        (!item.roles || item.roles.includes(userRole)) &&
        !(isPersonal && item.hideWhenPersonal)
    );
    if (!canAccess) {
      setActiveSection(SECTION_PROFILE);
    }
  }, [workspace, activeSection, userRole]);

  const [pushNotificationsEnabled, setPushNotificationsEnabled] = useState(!!initialPushNotificationsEnabled);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(!!initialEmailNotificationsEnabled);

  const handleSectionChange = useCallback((section) => {
    setActiveSection(section);
  }, []);

  const handleTogglePush = useCallback(
    (enabled) => {
      setPushNotificationsEnabled(enabled);
      if (onTogglePushNotifications) onTogglePushNotifications(enabled);
    },
    [onTogglePushNotifications]
  );

  const handleToggleEmail = useCallback(
    (enabled) => {
      setEmailNotificationsEnabled(enabled);
      if (onToggleEmailNotifications) onToggleEmailNotifications(enabled);
    },
    [onToggleEmailNotifications]
  );

  const renderContent = () => {
    switch (activeSection) {
      case SECTION_PROFILE:
        return <ProfilePanel userInfo={userInfo} onEditProfile={onEditProfile} onChangeProfilePicture={onChangeProfilePicture} i18nModule={i18nModule} />;
      case SECTION_SECURITY:
        return <SecurityPanel onChangePassword={onChangePassword} i18nModule={i18nModule} />;
      case SECTION_NOTIFICATIONS:
        return (
          <NotificationsPanel
            pushNotificationsEnabled={pushNotificationsEnabled}
            emailNotificationsEnabled={emailNotificationsEnabled}
            onTogglePushNotifications={handleTogglePush}
            onToggleEmailNotifications={handleToggleEmail}
            i18nModule={i18nModule}
          />
        );
      case SECTION_CONNECTIONS:
        return <McpConnectionPanel i18nModule={i18nModule} useEditorTheme={false} />;
      case SECTION_WS_GENERAL:
      case SECTION_WS_MEMBERS:
      case SECTION_WS_BILLING:
      case SECTION_WS_USAGE: {
        // All four workspace panels dereference `workspace.*` fields
        // unconditionally (workspace.workspaceName, workspace.usage,
        // workspace.reviewerUsage, workspace.plan…). Mounting them before
        // the async load resolves throws TypeError. Render a placeholder
        // until the controller has set workspace state.
        if (!workspace) {
          return <div className="settings-loading">Loading…</div>;
        }
        if (activeSection === SECTION_WS_GENERAL) {
          return <WsGeneralPanel workspace={workspace} userRole={userRole} i18nModule={i18nModule} onLeaveWorkspace={onLeaveWorkspace} onDeleteWorkspace={onDeleteWorkspace} />;
        }
        if (activeSection === SECTION_WS_MEMBERS) {
          // The default personal workspace also renders the Members panel.
          // For free users the Invite button is disabled (see
          // `inviteMemberDisabled` below); for paid users on the personal
          // workspace the click triggers backend workspace materialization.
          return <WsMembersPanel workspace={workspace} userRole={userRole} members={members} reviewers={reviewers} onInviteMember={onInviteMember} onRemoveMember={onRemoveMember} onRemoveReviewer={onRemoveReviewer} onSetMemberRole={onSetMemberRole} />;
        }
        if (activeSection === SECTION_WS_BILLING) {
          return <WsBillingPanel workspace={workspace} userRole={userRole} i18nModule={i18nModule} onUpgradePlan={onUpgradePlan} onUpdatePaymentMethod={onUpdatePaymentMethod} onChangePlan={onChangePlan} onCancelSubscription={onCancelSubscription} />;
        }
        if (activeSection === SECTION_WS_USAGE) {
          return <WsUsagePanel workspace={workspace} userRole={userRole} members={members} onPurchaseHourPack={onPurchaseHourPack} />;
        }
        return null;
      }
      default:
        return null;
    }
  };

  return (
    <div className="settings-container">
      <Sidebar
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        workspace={workspace}
        userRole={userRole}
        i18nModule={i18nModule}
        onUpgradePlan={onUpgradePlan}
      />
      <div className="settings-content">
        <div className="settings-content-inner">{renderContent()}</div>
      </div>
    </div>
  );
};

export default SettingsPage;
